import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/companies-that-build-websites-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/companies-that-build-websites-colorado")({
  head: () => ({
    meta: [
      { title: "Companies That Build Websites: How to Choose in Colorado (2026)" },
      { name: "description", content: "Comparing companies that build websites in Colorado? The four types you'll run into, what each really delivers, honest prices, and the questions that expose the pretenders." },
      { property: "og:title", content: "Companies That Build Websites: How to Choose in Colorado (2026)" },
      { property: "og:description", content: "The four types of website companies, what each really delivers, and honest Colorado prices." },
      { property: "og:url", content: `${SITE_URL}/learn/companies-that-build-websites-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Companies That Build Websites",
        description: ARTICLE?.desc ?? "How to choose between companies that build websites in Colorado.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/companies-that-build-websites-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Web Design"
      read="10 min read"
      title="Companies that build websites: how to choose one in Colorado"
      intro="Search for companies that build websites and you'll get freelancers, theme shops, big agencies, and specialists — all calling themselves the same thing. They are not the same thing. Here's how to tell them apart and what each should cost."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Companies that build websites" },
      ]}
      path="/learn/companies-that-build-websites-colorado"
      faqs={[
        { q: "What are the main types of companies that build websites?", a: "Four: freelancers (cheapest, variable quality, can disappear), theme shops (your logo in a pre-made template), big generalist agencies (account managers, high overhead, junior staff doing the work), and specialists who focus on one thing — like us, building lead-generating sites for Colorado service businesses for 27 years." },
        { q: "What should a small business website cost in Colorado?", a: "A real custom site runs $1,850 to $5,950 depending on page count. Our published packages: Starter $1,850 (4 pages), Pro $2,750 (10 pages with foundation SEO), Expert $5,950 (up to 50 pages). All include a year of hosting, SSL, and a traffic dashboard." },
        { q: "Should I hire a local Colorado company?", a: "For a local service business, yes — a company that knows Colorado knows your suburbs, your seasons, and what locals actually search. And accountability matters: our office is at 3210 E Woodmen Rd in Colorado Springs, and you can walk in." },
      ]}
    >
      <h2>The four types you'll run into</h2>
      <ul>
        <li><strong>Freelancers.</strong> Cheapest option and sometimes great — but one person means one point of failure. When they get busy or move on, your site is orphaned.</li>
        <li><strong>Theme shops.</strong> A $300–$800 &ldquo;website&rdquo; is a pre-made template with your logo dropped in. It looks fine in the demo and ranks nowhere.</li>
        <li><strong>Big generalist agencies.</strong> Impressive lobby, impressive invoice. You meet the sales director; a junior designer builds your site between other accounts.</li>
        <li><strong>Specialists.</strong> Companies that do one thing for one kind of client. We've spent 27 years building and rebuilding websites for Colorado service businesses — that's all we do.</li>
      </ul>

      <h2>Questions that expose the pretenders</h2>
      <ul>
        <li><strong>Who actually builds my site?</strong> If the answer is vague, it's outsourced.</li>
        <li><strong>Do I own the site and domain if I leave?</strong> The only correct answer is yes, in writing.</li>
        <li><strong>What's included after launch?</strong> Hosting, SSL, edits, a traffic dashboard — get the list in the proposal, not promises on a call.</li>
        <li><strong>Show me a site you built that ranks.</strong> A portfolio of pretty screenshots means nothing. Ask which of their clients show up on page one.</li>
        <li><strong>What happens if I cancel?</strong> Long contracts and &ldquo;you can't take the site with you&rdquo; exist to protect them, not you. Read our{" "}
          <Link to="/learn/escape-marketing-agency-contracts-colorado">guide to agency contract traps</Link> before signing anything.</li>
      </ul>

      <h2>What it should cost</h2>
      <p>
        Honest Colorado pricing: <strong>$1,850</strong> for a focused 4-page site,{" "}
        <strong>$2,750</strong> for a 10-page site with foundation SEO done for you,{" "}
        <strong>$5,950</strong> for up to 50 pages built service-by-city to rank. Extra pages are
        $85 each. Every package includes a year of hosting, SSL, and a traffic dashboard, and
        typical sites launch in under 7 business days. Full details on our{" "}
        <Link to="/pricing">pricing page</Link>.
      </p>

      <h2>What to do next</h2>
      <p>
        See what your new site would look like before you spend anything — request a{" "}
        <Link to="/free-mockup">free website mockup</Link>, or read about{" "}
        <Link to="/colorado-web-design">web design across Colorado</Link> and how we work.
      </p>
    </ArticleLayout>
  );
}
